/**
 * CR168: Hook for running flyer bucket discovery
 */

import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { DiscoveryResult } from "@/lib/flyer-bucket/types";

export interface EdgeFunctionError {
  status?: number;
  message: string;
  details?: string;
}

export function useFlyerBucketDiscovery() {
  const { toast } = useToast();
  const [discovering, setDiscovering] = useState(false);
  const [result, setResult] = useState<DiscoveryResult | null>(null);
  const [error, setError] = useState<EdgeFunctionError | null>(null);

  const runDiscovery = useCallback(async (bucketSourceId: string): Promise<DiscoveryResult | null> => {
    setDiscovering(true);
    setError(null);
    setResult(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("flyer-bucket-discover", {
        body: { bucket_source_id: bucketSourceId },
      });

      // Handle function-level errors (network, auth, etc.)
      if (fnError) {
        const status = (fnError as any)?.status || (fnError as any)?.context?.status;
        const errorDetails: EdgeFunctionError = {
          status,
          message: fnError.message || "Edge function invocation failed",
          details: JSON.stringify(fnError, null, 2),
        };
        
        console.error(`[useFlyerBucketDiscovery] Edge function error (status=${status || 'unknown'}):`, {
          message: errorDetails.message,
          status,
          rawError: fnError,
        });
        setError(errorDetails);
        
        toast({
          title: `Discovery Failed${status ? ` (${status})` : ''}`,
          description: `${errorDetails.message}. Check console for details.`,
          variant: "destructive",
        });
        return null;
      }

      // Handle application-level errors returned in the response body
      if (data?.error) {
        const errorDetails: EdgeFunctionError = {
          status: data.status || undefined,
          message: data.error,
          details: JSON.stringify(data, null, 2),
        };
        
        console.error(`[useFlyerBucketDiscovery] Application error:`, {
          error: data.error,
          message: data.message,
          fullResponse: data,
        });
        setError(errorDetails);
        
        const desc = data.message 
          ? `${data.error}: ${data.message}` 
          : data.error;
        
        toast({
          title: "Discovery Failed",
          description: `${desc}. Check console for details.`,
          variant: "destructive",
        });
        return null;
      }

      const discoveryResult = data as DiscoveryResult;
      setResult(discoveryResult);
      
      toast({
        title: "Discovery Complete",
        description: `Found ${discoveryResult.summary.eligible_count} eligible flyers out of ${discoveryResult.summary.total_files} total`,
      });

      return discoveryResult;
    } catch (err: any) {
      const errorDetails: EdgeFunctionError = {
        message: err.message || "Unexpected error during discovery",
        details: err.stack || String(err),
      };
      
      console.error("[useFlyerBucketDiscovery] Unexpected error:", {
        message: errorDetails.message,
        stack: err.stack,
        error: err,
      });
      setError(errorDetails);
      
      toast({
        title: "Discovery Failed",
        description: `${errorDetails.message}. Check console for details.`,
        variant: "destructive",
      });
      return null;
    } finally {
      setDiscovering(false);
    }
  }, [toast]);

  const clearResult = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return {
    discovering,
    result,
    error,
    runDiscovery,
    clearResult,
  };
}
